import { useCallback, useMemo, useState } from 'react';
import { Copy, Film } from 'lucide-react';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, IconButton, Input, Skeleton } from '@vnetwork/ui/primitives';
/** Structurally identical to react-table's RowSelectionState — @partner cannot import tanstack directly. */
type RowSel = Record<string, boolean>;
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useRecordings,
  useResendRecordingNotification,
  useMergeRecordings,
} from '@/api/media/live-live-streams';
import type { RecordingItem, LiveStreamItem } from '@/api/media/live-live-streams-types';
import styles from './RecordingTab.module.css';

interface Props {
  serviceId: string;
  stream: LiveStreamItem;
}

type PendingAction =
  | { kind: 'resend'; row: RecordingItem }
  | { kind: 'merge'; uuids: string[] }
  | null;

const fmtTs = (ts?: number) =>
  ts ? new Date(ts).toLocaleString('en-GB').replace(',', '') : '—';

/** Recording status code → label (numeric string from API). */
function recordingStatusLabel(status?: string): string {
  if (status === '6') return 'Completed';
  if (status === '1') return 'Recording';
  if (status === '0') return 'Pending';
  return status || '—';
}

function recordingStatusTone(status?: string): 'success' | 'info' | 'neutral' | 'warning' {
  if (status === '6') return 'success';
  if (status === '1') return 'info';
  if (status === '0') return 'neutral';
  return 'warning';
}

/** Inline read-only URL cell with copy button. */
function UrlCell({ url }: { url: string }) {
  const toast = useToast();
  const copyIcon = <Copy size={14} />;
  return (
    <div className={styles.urlRow}>
      <Input
        inputSize="sm"
        value={url || ''}
        readOnly
        wrapperClassName={styles.urlInput}
      />
      {url && (
        <IconButton
          className={styles.copyBtn}
          variant="ghost"
          size="sm"
          aria-label="Copy recording URL"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(url).catch(() => {});
            toast.success('URL copied');
          }}
        >
          {copyIcon}
        </IconButton>
      )}
    </div>
  );
}

export function RecordingTab({ serviceId, stream }: Props) {
  const toast = useToast();

  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const { data: recordings = [], isLoading, isError, refetch } = useRecordings(
    serviceId,
    stream.id,
  );

  const resendMut = useResendRecordingNotification(serviceId, stream.id, {
    onSuccess: () => { toast.success('Notification resent'); setPendingAction(null); },
    onError: (err) =>
      toast.error((err as { message?: string })?.message || 'Failed to resend notification'),
  });

  const mergeMut = useMergeRecordings(serviceId, stream.id, {
    onSuccess: () => {
      toast.success('Recordings merged');
      setSelectedUuids([]);
      setPendingAction(null);
      refetch();
    },
    onError: (err) =>
      toast.error((err as { message?: string })?.message || 'Failed to merge recordings'),
  });

  const handleConfirm = useCallback(() => {
    if (!pendingAction) return;
    if (pendingAction.kind === 'resend') resendMut.mutate(pendingAction.row.uuid);
    else mergeMut.mutate(pendingAction.uuids);
  }, [pendingAction, resendMut, mergeMut]);

  const isBusy = resendMut.isPending || mergeMut.isPending;

  const columns = useMemo<ColumnDef<RecordingItem, unknown>[]>(
    () => [
      {
        id: 'preset_name',
        header: 'Preset',
        size: 150,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.preset_name || '—'}</span>
        ),
      },
      {
        id: 'started',
        header: 'Started',
        size: 160,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtTs(row.original.started)}</span>
        ),
      },
      {
        id: 'ended',
        header: 'Ended',
        size: 160,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtTs(row.original.ended)}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => (
          <StatusPill tone={recordingStatusTone(row.original.status)}>
            {recordingStatusLabel(row.original.status)}
          </StatusPill>
        ),
      },
      {
        id: 'url',
        header: 'URL',
        cell: ({ row }) => <UrlCell url={row.original.url} />,
      },
      {
        id: 'actions',
        header: '',
        size: 170,
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPendingAction({ kind: 'resend', row: row.original })}
            >
              Resend Notification
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={52} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert tone="warning" title="Failed to load recordings">
        Could not retrieve recordings for this stream. Try refreshing the page.
      </Alert>
    );
  }

  const confirmTitle =
    pendingAction?.kind === 'merge'
      ? `Merge ${pendingAction.uuids.length} recordings?`
      : 'Resend notification?';

  const confirmDesc =
    pendingAction?.kind === 'merge'
      ? `Merge ${pendingAction.uuids.length} selected recording segments into a single output?`
      : pendingAction?.kind === 'resend'
        ? `Resend completion notification for recording "${pendingAction.row.preset_name}"?`
        : '';

  return (
    <div>
      {recordings.length > 0 && (
        <div className={styles.toolbar}>
          <Button
            variant="secondary"
            size="sm"
            disabled={selectedUuids.length < 2 || isBusy}
            onClick={() => setPendingAction({ kind: 'merge', uuids: selectedUuids })}
          >
            Merge Recordings ({selectedUuids.length} selected)
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={recordings}
        getRowId={(r) => r.uuid}
        enableSelection
        rowSelection={Object.fromEntries(selectedUuids.map((id) => [id, true]))}
        onRowSelectionChange={(updater) => {
          const prev: RowSel = Object.fromEntries(selectedUuids.map((id) => [id, true]));
          const next: RowSel = typeof updater === 'function' ? updater(prev) : (updater as RowSel);
          setSelectedUuids(Object.keys(next).filter((k) => next[k]));
        }}
        dense
        emptyState={
          <EmptyState
            icon={<Film size={20} />}
            title="No recordings"
            description="No recordings have been created for this stream."
          />
        }
      />

      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(o) => !o && setPendingAction(null)}
        title={confirmTitle}
        description={confirmDesc}
        confirmLabel={pendingAction?.kind === 'merge' ? 'Merge' : 'Resend'}
        destructive={false}
        loading={isBusy}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

export default RecordingTab;
